import { useRef, useState, useEffect } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Smartphone, Layers, Code2, Globe, Shield, Database, Home, Key, Wrench, Hotel, Wifi, ChevronRight } from 'lucide-react';

const techFeatures = [
{
  icon: Smartphone,
  title: 'Horizon App',
  description: 'Super app for living - buy, sell, rent, manage homes, access maintenance, control smart infrastructure.'
},
{
  icon: Layers,
  title: 'Hemraj One',
  description: 'Internal operating system managing properties, inventory, services, staff across all companies.'
},
{
  icon: Code2,
  title: "CodeWorks",
  description: 'Building digital platforms and software systems for internal and external clients.'
},
{
  icon: Globe,
  title: 'Connected Ecosystem',
  description: 'Seamless integration between all Hemraj companies and services through unified technology.'
},
{
  icon: Shield,
  title: 'Security Infrastructure',
  description: 'Integrated safety and access management systems powered by Hifazat.'
},
{
  icon: Database,
  title: 'Smart Infrastructure',
  description: 'Future-ready buildings with IoT integration and intelligent automation.'
}];


export const Technology = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="technology"
      data-testid="technology-section"
      ref={ref}
      className="py-24 lg:py-32 bg-[#F9F6F0]">

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="max-w-2xl mb-16 lg:mb-24">
          <motion.p
            className="overline mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}>

            Technology Layer
          </motion.p>

          <motion.h2
            data-testid="technology-title"
            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#1A1A1A]"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}>

            Digital Infrastructure for Modern Living
          </motion.h2>

          <motion.p
            className="mt-6 text-base text-[#4A4A4A] leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}>

            Technology connects every aspect of the Hemraj ecosystem, enabling seamless 
            experiences from property discovery to daily living.
          </motion.p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-black/10">
          {techFeatures.map((feature, index) => {
            const IconComponent = feature.icon;
            return (
              <motion.div
                key={feature.title}
                data-testid={`tech-feature-${feature.title.toLowerCase().replace(/\s+/g, '-')}`}
                className="bg-[#F9F6F0] p-8 lg:p-12 group cursor-pointer hover:bg-[#EFEBE4] transition-colors duration-500"
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}>

                <div className="w-12 h-12 bg-[#1A1A1A] flex items-center justify-center mb-6 transition-colors duration-500 group-hover:bg-[#C5A880]">
                  <IconComponent className="w-5 h-5 text-[#F9F6F0]" />
                </div>

                <h3 className="font-heading text-xl lg:text-2xl font-light text-[#1A1A1A] mb-3">
                  {feature.title}
                </h3>

                <p className="text-sm text-[#4A4A4A] leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>);

          })}
        </div>

        {/* App Preview Section */}
        <motion.div
          className="mt-16 lg:mt-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}>

          <div>
            <h3 className="font-heading text-2xl lg:text-3xl font-light text-[#1A1A1A] mb-6">One App for Smooth Life

            </h3>
            <p className="text-base font-medium text-[#4A4A4A] leading-relaxed mb-8">
              Horizon App brings together property management, home services, hospitality bookings, 
              smart home controls, and community features into a single, elegant interface.
            </p>
            <div className="flex flex-wrap gap-4">
              <span className="px-4 py-2 bg-[#1A1A1A] text-[#F9F6F0] text-xs uppercase tracking-wider font-medium">
                Buy & Sell
              </span>
              <span className="px-4 py-2 bg-[#1A1A1A] text-[#F9F6F0] text-xs uppercase tracking-wider font-medium">
                Rent & Manage
              </span>
              <span className="px-4 py-2 bg-[#1A1A1A] text-[#F9F6F0] text-xs uppercase tracking-wider font-medium">
                Maintain
              </span>
              <span className="px-4 py-2 bg-[#1A1A1A] text-[#F9F6F0] text-xs uppercase tracking-wider font-medium">
                Book Hotels
              </span>
              <span className="px-4 py-2 bg-[#1A1A1A] text-[#F9F6F0] text-xs uppercase tracking-wider font-medium">
                Smart Home
              </span>
            </div>
          </div>

          {/* Animated User Journey Phone */}
          <div className="relative">
            <HorizonAppDemo isInView={isInView} />
            <p className="text-center text-xs text-[#4A4A4A] mt-8 font-medium">
              *Early demo for marketing purposes only. Final app may vary.
            </p>
          </div>
        </motion.div>
      </div>
    </section>);

};

// Animated Horizon App Demo Component
const userJourneySteps = [
  { id: 1, icon: Home, title: 'Find Your Dream Home', subtitle: 'Browse premium properties', color: '#C5A880' },
  { id: 2, icon: Key, title: 'Secure Your Space', subtitle: 'Easy booking & documentation', color: '#1A1A1A' },
  { id: 3, icon: Wrench, title: 'Maintain with Ease', subtitle: 'One-tap service requests', color: '#C5A880' },
  { id: 4, icon: Hotel, title: 'Book Experiences', subtitle: 'Hotels, events & more', color: '#1A1A1A' },
  { id: 5, icon: Wifi, title: 'Smart Living', subtitle: 'Control your environment', color: '#C5A880' },
];

const HorizonAppDemo = ({ isInView }) => {
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    if (!isInView) return;
    
    const interval = setInterval(() => {
      setCurrentStep((prev) => (prev + 1) % userJourneySteps.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [isInView]);

  const step = userJourneySteps[currentStep];
  const StepIcon = step.icon;

  return (
    <div className="aspect-[9/19] max-w-[260px] mx-auto">
      <div className="w-full h-full bg-[#F9F6F0] rounded-[2.5rem] flex flex-col overflow-hidden shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25),0_0_0_1px_rgba(0,0,0,0.05)]">
        {/* Status Bar */}
        <div className="flex-shrink-0 flex justify-between items-center px-5 pt-4 pb-2">
          <span className="text-[10px] font-medium text-[#1A1A1A]">9:41</span>
          <div className="flex gap-1">
            <div className="w-3 h-1.5 bg-[#1A1A1A] rounded-sm"></div>
          </div>
        </div>

        {/* App Header */}
        <div className="flex-shrink-0 px-5 py-3 border-b border-black/10">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center justify-between"
          >
            <span className="font-heading text-base font-medium text-[#1A1A1A]">Horizon</span>
            <div className="w-7 h-7 bg-[#1A1A1A] rounded-full flex items-center justify-center">
              <span className="text-[#F9F6F0] text-[10px] font-medium">PN</span>
            </div>
          </motion.div>
        </div>

        {/* Main Content - Animated */}
        <div className="flex-1 min-h-0 px-5 py-3 flex flex-col justify-center items-center relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, scale: 0.8, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 1.1, y: -15 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="text-center flex flex-col items-center justify-center"
            >
              {/* Icon with glow */}
              <motion.div 
                className="relative mb-3"
                animate={{ 
                  boxShadow: [
                    `0 0 15px ${step.color}20`,
                    `0 0 30px ${step.color}40`,
                    `0 0 15px ${step.color}20`
                  ]
                }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <div 
                  className="w-14 h-14 flex items-center justify-center"
                  style={{ backgroundColor: step.color }}
                >
                  <StepIcon className="w-7 h-7 text-[#F9F6F0]" />
                </div>
              </motion.div>

              <h4 className="font-heading text-base font-medium text-[#1A1A1A] mb-1 leading-tight">
                {step.title}
              </h4>
              <p className="text-[10px] font-medium text-[#4A4A4A]">
                {step.subtitle}
              </p>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Progress Dots - Inside screen at bottom */}
        <div className="flex-shrink-0 pb-4 flex justify-center gap-1.5">
          {userJourneySteps.map((_, idx) => (
            <motion.div
              key={idx}
              className="w-1.5 h-1.5 rounded-full"
              animate={{
                backgroundColor: idx === currentStep ? '#C5A880' : '#1A1A1A20',
                scale: idx === currentStep ? 1.3 : 1
              }}
              transition={{ duration: 0.3 }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};